'use client';

import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { Menu, X, ArrowUpRight } from 'lucide-react';
import { Logo } from '@/components/shared/logo';
import { cn } from '@/lib/utils';

const FOCUSABLE = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';

const NAV_LINKS: ReadonlyArray<{ label: string; href: string }> = [
  { label: 'How it works', href: '#how-it-works' },
  { label: 'Case studies', href: '#cases' },
  { label: 'Services', href: '#services' },
  { label: 'Security', href: '#security' },
];

export function MobileNav() {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  useEffect(() => {
    if (open) {
      wasOpenRef.current = true;
      dialogRef.current?.querySelector<HTMLElement>(FOCUSABLE)?.focus();
    } else if (wasOpenRef.current) {
      triggerRef.current?.focus();
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false);
        return;
      }
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = Array.from(dialogRef.current.querySelectorAll<HTMLElement>(FOCUSABLE));
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last?.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first?.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={cn(
          'md:hidden inline-flex items-center justify-center h-9 w-9 rounded-md',
          'border border-(--color-border) text-(--color-text-muted)',
          'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
          'hover:border-(--color-border-hover) hover:text-(--color-text)',
          'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
        )}
        aria-label="Open menu"
        aria-expanded={open}
        aria-controls="mobile-nav-dialog"
        onClick={() => setOpen(true)}
      >
        <Menu size={20} strokeWidth={1.5} aria-hidden="true" />
      </button>

      {open && (
        <div
          ref={dialogRef}
          id="mobile-nav-dialog"
          role="dialog"
          aria-modal="true"
          aria-label="Navigation menu"
          className="fixed inset-0 z-50 flex flex-col bg-(--color-bg)/95 backdrop-blur-2xl md:hidden"
        >
          <div className="flex h-(--nav-h) shrink-0 items-center justify-between border-b border-(--color-border) px-6">
            <Logo />
            <button
              type="button"
              className={cn(
                'inline-flex items-center justify-center h-9 w-9 rounded-md',
                'border border-(--color-border) text-(--color-text-muted)',
                'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                'hover:border-(--color-border-hover) hover:text-(--color-text)',
                'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
              )}
              aria-label="Close menu"
              onClick={() => setOpen(false)}
            >
              <X size={20} strokeWidth={1.5} aria-hidden="true" />
            </button>
          </div>

          <nav aria-label="Mobile navigation" className="flex flex-col px-6 pt-8">
            {NAV_LINKS.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setOpen(false)}
                className={cn(
                  'border-b border-(--color-border) py-5',
                  'text-2xl font-semibold text-(--color-text)',
                  'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                  'hover:text-(--color-accent)',
                  'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
                )}
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="mt-auto px-6 pb-12">
            <Link
              href="#contact"
              onClick={() => setOpen(false)}
              className={cn(
                'flex items-center justify-center gap-2 h-12 w-full rounded-md',
                'bg-(--color-accent) text-(--color-bg)',
                'text-base font-medium',
                'transition-colors duration-[var(--duration-fast)] ease-[var(--ease-out)]',
                'hover:bg-(--color-accent-hover)',
                'focus-visible:outline-2 focus-visible:outline-(--color-accent) focus-visible:outline-offset-2',
              )}
            >
              Book a call
              <ArrowUpRight size={16} strokeWidth={1.5} aria-hidden="true" />
            </Link>
          </div>
        </div>
      )}
    </>
  );
}
